import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  Alert,
  Divider,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import {
  Search,
  Visibility,
  DirectionsCar,
  Person,
  LocationOn,
  Schedule,
  AttachMoney,
  Cancel,
  CheckCircle,
  Refresh,
  FilterList
} from '@mui/icons-material';
import axios from 'axios';

interface Ride {
  id: string;
  rideNumber: number;
  driverId: string;
  originCityId: string;
  destinationCityId: string;
  departureDateTime: string;
  estimatedDuration?: number;
  availableSeats: number;
  pricePerPerson: number;
  status: string;
  createdAt: string;
  driver: {
    id: string;
    memberId?: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    phoneNumber: string;
    profilePicture?: string;
    driverRating: number;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  bookings: Booking[];
  vehicle?: {
    make: string;
    model: string;
    year: number;
    color: string;
    licensePlate: string;
  };
}

interface Booking {
  id: string;
  bookingNumber: number;
  passengerId: string;
  seatsBooked: number;
  totalPrice: number;
  status: string;
  createdAt: string;
  passenger: {
    id: string;
    memberId?: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    phoneNumber: string;
  };
}

interface RideRequest {
  id: string;
  passengerId: string;
  originCityId: string;
  destinationCityId: string;
  preferredDateTime: string;
  passengerCount: number;
  maxBudget: number;
  status: string;
  createdAt: string;
  passenger: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    phoneNumber: string;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  bids: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const RidesPage: React.FC = () => {
  const [mainTabValue, setMainTabValue] = useState(0);

  // Offered Rides States
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRides, setTotalRides] = useState(0);

  // Requested Rides States
  const [requests, setRequests] = useState<RideRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsPage, setRequestsPage] = useState(0);
  const [requestsRowsPerPage, setRequestsRowsPerPage] = useState(25);
  const [totalRequests, setTotalRequests] = useState(0);

  // Shared Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Dialog States
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info', message: string } | null>(null);

  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    if (mainTabValue === 0) {
      fetchRides();
      fetchStats();
    } else {
      fetchRequests();
    }
  }, [mainTabValue, page, rowsPerPage, requestsPage, requestsRowsPerPage, search, filterStatus]);

  const fetchRides = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(search && { search }),
        ...(filterStatus !== 'ALL' && { status: filterStatus })
      });

      const response = await axios.get(`${apiUrl}/admin/rides?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRides(response.data.rides || []);
      setTotalRides(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch rides:', error);
      showAlert('error', 'Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (requestsPage + 1).toString(),
        limit: requestsRowsPerPage.toString(),
        ...(search && { search }),
        ...(filterStatus !== 'ALL' && { status: filterStatus })
      });

      const response = await axios.get(`${apiUrl}/admin/rides/requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequests(response.data.requests || []);
      setTotalRequests(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch requested rides:', error);
      showAlert('error', 'Failed to load requested rides');
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const metrics = response.data.metrics;
      setStats({
        total: metrics.totalRides || 0,
        upcoming: metrics.upcomingRides || 0,
        inProgress: metrics.activeRides || 0,
        completed: metrics.completedRides || 0,
        cancelled: metrics.cancelledRides || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleViewRide = (ride: Ride) => {
    setSelectedRide(ride);
    setDetailsDialog(true);
  };

  const handleCancelRide = async (rideId: string) => {
    if (!window.confirm('Are you sure you want to cancel this ride?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/rides/${rideId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Ride cancelled successfully');
      fetchRides();
      setDetailsDialog(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to cancel ride');
    }
  };

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'OFFERED':
      case 'OPEN':
        return 'info';
      case 'BOOKED':
      case 'CLOSED':
        return 'primary';
      case 'IN_PROGRESS':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalBookedSeats = (ride: Ride) => {
    return ride.bookings?.reduce((sum, booking) => {
      if (booking.status === 'CONFIRMED') {
        return sum + booking.seatsBooked;
      }
      return sum;
    }, 0) || 0;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Ride Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage all rides on the platform
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            if (mainTabValue === 0) {
              fetchRides();
              fetchStats();
            } else {
              fetchRequests();
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Main Tabs for Offered vs Requested */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTabValue} onChange={(e, newValue) => {
          setMainTabValue(newValue);
          setSearch('');
          setFilterStatus('ALL');
          setPage(0);
          setRequestsPage(0);
        }}>
          <Tab label="Offered Rides" />
          <Tab label="Requested Rides" />
        </Tabs>
      </Box>

      {/* Stats Overview - Only for Offered Rides right now */}
      {mainTabValue === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <DirectionsCar color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">Total Rides</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Schedule color="info" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.upcoming}
            </Typography>
            <Typography variant="body2" color="text.secondary">Upcoming</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <DirectionsCar color="warning" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.inProgress}
            </Typography>
            <Typography variant="body2" color="text.secondary">In Progress</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary">Completed</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Cancel color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight="bold">
              {stats.cancelled}
            </Typography>
            <Typography variant="body2" color="text.secondary">Cancelled</Typography>
          </Paper>
        </Box>
      )}

      {/* Filters (Shared) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by user, origin, or destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              {mainTabValue === 0 ? [
                <MenuItem key="OFFERED" value="OFFERED">Offered</MenuItem>,
                <MenuItem key="BOOKED" value="BOOKED">Booked</MenuItem>,
                <MenuItem key="IN_PROGRESS" value="IN_PROGRESS">In Progress</MenuItem>,
                <MenuItem key="COMPLETED" value="COMPLETED">Completed</MenuItem>,
                <MenuItem key="CANCELLED" value="CANCELLED">Cancelled</MenuItem>,
              ] : [
                <MenuItem key="OPEN" value="OPEN">Open</MenuItem>,
                <MenuItem key="CLOSED" value="CLOSED">Closed</MenuItem>,
                <MenuItem key="EXPIRED" value="EXPIRED">Expired</MenuItem>,
              ]}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => {
            setSearch('');
            setFilterStatus('ALL');
          }}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Rides Table */}
      {mainTabValue === 0 ? (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': { py: 1, px: 1, fontSize: '1.2rem' },
                '& .MuiTableCell-head': { fontSize: '1.3rem', fontWeight: 'bold' },
                '& .MuiTypography-body1': { fontSize: '1.3rem' },
                '& .MuiTypography-body2': { fontSize: '1.2rem' },
                '& .MuiTypography-caption': { fontSize: '1.1rem', display: 'block' },
                '& .MuiChip-root': { fontSize: '1.1rem', height: '32px' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Ride #</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Departure</TableCell>
                  <TableCell>Seats</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No offered rides found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rides.map((ride) => {
                    const bookedSeats = getTotalBookedSeats(ride);
                    return (
                      <TableRow key={ride.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            #{ride.rideNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar src={ride.driver.profilePicture}>
                              {ride.driver.firstName[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {ride.driver.preferredName || ride.driver.firstName} {ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ⭐ {ride.driver.driverRating?.toFixed(1) || 'N/A'} • {ride.driver.memberId || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {ride.originCity.name} → {ride.destinationCity.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ride.originCity.province} to {ride.destinationCity.province}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(ride.departureDateTime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bookedSeats} / {ride.availableSeats}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            ${ride.pricePerPerson}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ride.status.replace(/_/g, ' ')}
                            size="small"
                            color={getStatusColor(ride.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleViewRide(ride)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRides}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': { py: 1, px: 1, fontSize: '1.2rem' },
                '& .MuiTableCell-head': { fontSize: '1.3rem', fontWeight: 'bold' },
                '& .MuiTypography-body1': { fontSize: '1.3rem' },
                '& .MuiTypography-body2': { fontSize: '1.2rem' },
                '& .MuiTypography-caption': { fontSize: '1.1rem', display: 'block' },
                '& .MuiChip-root': { fontSize: '1.1rem', height: '32px' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Passenger</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Preferred Date</TableCell>
                  <TableCell>Passengers</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell>Bids</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requestsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No requested rides found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((req) => (
                    <TableRow key={req.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar>
                            {req.passenger.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {req.passenger.preferredName || req.passenger.firstName} {req.passenger.lastName ? req.passenger.lastName.charAt(0) + '.' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {req.passenger.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {req.originCity.name} → {req.destinationCity.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {req.originCity.province} to {req.destinationCity.province}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(req.preferredDateTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {req.passengerCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          ${req.maxBudget ?? 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {req.bids?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status}
                          size="small"
                          color={getStatusColor(req.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRequests}
            page={requestsPage}
            onPageChange={(e, newPage) => setRequestsPage(newPage)}
            rowsPerPage={requestsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setRequestsRowsPerPage(parseInt(e.target.value, 10));
              setRequestsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Ride Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Ride Details {selectedRide && ` - #${selectedRide.rideNumber}`}
        </DialogTitle>
        <DialogContent>
          {selectedRide && (
            <Box>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Overview" />
                <Tab label="Bookings" />
                <Tab label="Driver & Vehicle" />
              </Tabs>

              {/* Overview Tab */}
              <TabPanel value={tabValue} index={0}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Route</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRide.originCity.name}, {selectedRide.originCity.province} → {selectedRide.destinationCity.name}, {selectedRide.destinationCity.province}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Departure Time</Typography>
                    <Typography variant="body1">{formatDateTime(selectedRide.departureDateTime)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Est. Duration</Typography>
                    <Typography variant="body1">{selectedRide.estimatedDuration ? `${selectedRide.estimatedDuration} mins` : 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Available Seats</Typography>
                    <Typography variant="body1">{selectedRide.availableSeats}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Price Per Seat</Typography>
                    <Typography variant="body1">${selectedRide.pricePerPerson}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedRide.status.replace(/_/g, ' ')}
                        color={getStatusColor(selectedRide.status)}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body1">{formatDateTime(selectedRide.createdAt)}</Typography>
                  </Box>
                </Stack>
              </TabPanel>

              {/* Bookings Tab */}
              <TabPanel value={tabValue} index={1}>
                {selectedRide.bookings && selectedRide.bookings.length > 0 ? (
                  <Stack spacing={2}>
                    {selectedRide.bookings.map((booking) => (
                      <Paper key={booking.id} sx={{ p: 2 }} elevation={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                              Booking #{booking.bookingNumber}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {booking.passenger.preferredName || booking.passenger.firstName} {booking.passenger.lastName ? booking.passenger.lastName.charAt(0) + '.' : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                              Member ID: {booking.passenger.memberId || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {booking.passenger.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {booking.passenger.phoneNumber}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Seats: {booking.seatsBooked}
                            </Typography>
                            <Typography variant="body2">
                              Total: ${booking.totalPrice}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Booked: {formatDateTime(booking.createdAt)}
                            </Typography>
                          </Box>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={booking.status === 'CONFIRMED' ? 'success' : 'default'}
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No bookings yet</Alert>
                )}
              </TabPanel>

              {/* Driver & Vehicle Tab */}
              <TabPanel value={tabValue} index={2}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Driver Information</Typography>
                    <Paper sx={{ p: 2 }} elevation={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar src={selectedRide.driver.profilePicture} sx={{ width: 56, height: 56 }}>
                          {selectedRide.driver.firstName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedRide.driver.preferredName || selectedRide.driver.firstName} {selectedRide.driver.lastName ? selectedRide.driver.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ⭐ {selectedRide.driver.driverRating?.toFixed(1) || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>Member ID: {selectedRide.driver.memberId || 'N/A'}</Typography>
                      <Typography variant="body2">Email: {selectedRide.driver.email}</Typography>
                      <Typography variant="body2">Phone: {selectedRide.driver.phoneNumber}</Typography>
                    </Paper>
                  </Box>

                  {selectedRide.vehicle && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Vehicle Information</Typography>
                      <Paper sx={{ p: 2 }} elevation={2}>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedRide.vehicle.year} {selectedRide.vehicle.make} {selectedRide.vehicle.model}
                        </Typography>
                        <Typography variant="body2">Color: {selectedRide.vehicle.color}</Typography>
                        <Typography variant="body2">License Plate: {selectedRide.vehicle.licensePlate}</Typography>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </TabPanel>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRide && selectedRide.status !== 'CANCELLED' && selectedRide.status !== 'COMPLETED' && (
            <Button
              onClick={() => handleCancelRide(selectedRide.id)}
              color="error"
              variant="outlined"
            >
              Cancel Ride
            </Button>
          )}
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RidesPage;
